// Spec 47 approval-ux — cmp_approval_break_glass_banner / break_glass_banner.
//
// "Non-dismissable top-of-card banner indicating the current approver is
// acting under a break-glass grant. Shows grant id, TTL remaining, and
// the justification text. Turns from state_warning to state_danger at
// T-15min from expiry." (spec 47 line 853)
//
// a11y per spec 47:
//   - role='status' with aria-live='polite'
//   - TTL updates do NOT interrupt screen reader — only major state
//     transitions (warning→danger, active→expired) are announced
//   - Banner is NOT dismissable — no close button rendered
//   - Justification expand uses aria-expanded on its trigger

import { forwardRef, useState, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export type BreakGlassState = "active" | "expiring_soon" | "expired";

export interface BreakGlassBannerProps extends HTMLAttributes<HTMLDivElement> {
  readonly grantId: string;
  /** Pre-formatted TTL string ("2h 15m remaining"). Caller updates as time elapses. */
  readonly ttlText: string;
  readonly justification: string;
  readonly state: BreakGlassState;
  /** Click handler for the audit-event link. Renders link only when set. */
  readonly auditUrl?: string;
  /** Click handler for "Reauthorize" CTA. Renders only when state === "expiring_soon" or "expired". */
  readonly onReauthorize?: () => void;
  /** Override default English title ("Break-glass active — {grantId}"). */
  readonly titleOverride?: string;
  /** Override label for the show-justification toggle. */
  readonly showJustificationLabel?: string;
  /** Override label for the hide-justification toggle. */
  readonly hideJustificationLabel?: string;
  /** Override label for the reauthorize CTA. */
  readonly reauthorizeLabel?: string;
  /** Override label for the audit-link. */
  readonly viewAuditLabel?: string;
}

const STATE_TO_ROLE: Readonly<Record<BreakGlassState, "status" | "alert">> = {
  // active + expiring_soon use polite (status). Expired escalates to alert
  // (assertive) because the approver is now operating without authority.
  active: "status",
  expiring_soon: "status",
  expired: "alert",
};

export const BreakGlassBanner = forwardRef<HTMLDivElement, BreakGlassBannerProps>(
  (
    {
      grantId,
      ttlText,
      justification,
      state,
      auditUrl,
      onReauthorize,
      titleOverride,
      showJustificationLabel = "Show justification",
      hideJustificationLabel = "Hide justification",
      reauthorizeLabel = "Reauthorize",
      viewAuditLabel = "View audit",
      className,
      ...rest
    },
    ref,
  ) => {
    const [expanded, setExpanded] = useState(false);
    const role = STATE_TO_ROLE[state];
    const ariaLive = role === "alert" ? "assertive" : "polite";
    const title = titleOverride ?? `Break-glass active — ${grantId}`;
    const showReauthorize =
      onReauthorize !== undefined && (state === "expiring_soon" || state === "expired");

    return (
      <div
        ref={ref}
        role={role}
        aria-live={ariaLive}
        data-component="break-glass-banner"
        data-state={state}
        className={cx("nai-break-glass-banner", `nai-break-glass-banner--${state}`, className)}
        {...rest}
      >
        <span aria-hidden="true" className="nai-break-glass-banner-icon">
          {/* shield-warning placeholder — caller can replace via slot in future */}
          ⚠
        </span>
        <div className="nai-break-glass-banner-body">
          <div data-component="break-glass-banner-title" className="nai-break-glass-banner-title">
            {title}
          </div>
          <div data-component="break-glass-banner-ttl" className="nai-break-glass-banner-ttl">
            {ttlText}
          </div>
          <button
            type="button"
            data-component="break-glass-banner-justification-toggle"
            className="nai-break-glass-banner-justification-toggle"
            aria-expanded={expanded}
            onClick={() => setExpanded((e) => !e)}
          >
            {expanded ? hideJustificationLabel : showJustificationLabel}
          </button>
          {expanded && (
            <div
              data-component="break-glass-banner-justification"
              className="nai-break-glass-banner-justification"
            >
              {justification}
            </div>
          )}
        </div>
        <div className="nai-break-glass-banner-actions">
          {auditUrl !== undefined && (
            <a
              href={auditUrl}
              data-component="break-glass-banner-audit-link"
              className="nai-break-glass-banner-audit-link"
            >
              {viewAuditLabel}
            </a>
          )}
          {showReauthorize && (
            <button
              type="button"
              data-component="break-glass-banner-reauthorize"
              className="nai-break-glass-banner-reauthorize"
              onClick={onReauthorize}
            >
              {reauthorizeLabel}
            </button>
          )}
        </div>
        {/* IMPORTANT: NO close button — spec 47 line 887: "Banner is NOT
            dismissable — no close button is rendered". */}
      </div>
    );
  },
);
BreakGlassBanner.displayName = "BreakGlassBanner";
